import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  User,
  Briefcase,
  DollarSign,
  Building2,
  Phone,
  Upload,
  X,
  Eye,
  FileText,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const EmployeeFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAdminAuth();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Personal Information
  const [personalInfo, setPersonalInfo] = useState({
    fullName: '',
    nationalId: '',
    birthDate: '',
    nationality: 'سعودي',
    gender: 'male',
    maritalStatus: 'single',
    address: '',
    phone: '',
    email: ''
  });

  // Job Information
  const [jobInfo, setJobInfo] = useState({
    employeeNumber: '',
    position: '',
    department: '',
    grade: '',
    directManager: '',
    workType: 'full-time',
    attendanceSystem: 'fingerprint',
    workLocation: 'office',
    hireDate: '',
    contractType: 'unlimited',
    employmentStatus: 'active',
    workDays: 'sunday-thursday',
    workHours: '08:00-17:00'
  });

  // Salary Information
  const [salaryInfo, setSalaryInfo] = useState({
    basicSalary: '',
    housingAllowance: '',
    transportAllowance: '',
    otherAllowances: '',
    notes: ''
  });

  // Bank Information
  const [bankInfo, setBankInfo] = useState({
    bankName: '',
    accountNumber: '',
    iban: ''
  });

  // Emergency Contact
  const [emergencyContact, setEmergencyContact] = useState({
    name: '',
    phone: '',
    relationship: ''
  });

  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Calculate age from birth date
  const [calculatedAge, setCalculatedAge] = useState('');

  useEffect(() => {
    if (personalInfo.birthDate) {
      const birth = new Date(personalInfo.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setCalculatedAge(age > 0 ? `${age} سنة` : '');
    } else {
      setCalculatedAge('');
    }
  }, [personalInfo.birthDate]);

  // Generate employee number
  useEffect(() => {
    if (!isEditMode && !jobInfo.employeeNumber) {
      const randomNum = Math.floor(Math.random() * 9000) + 1000;
      setJobInfo(prev => ({ ...prev, employeeNumber: `EMP-${randomNum}` }));
    }
  }, [isEditMode]);

  // Load employee data if editing
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      // Mock data for editing
      setTimeout(() => {
        setPersonalInfo({
          fullName: 'أحمد محمد العتيبي',
          nationalId: '1234567890',
          birthDate: '1990-05-15',
          nationality: 'سعودي',
          gender: 'male',
          maritalStatus: 'married',
          address: 'الرياض، حي النرجس',
          phone: '+966501234567',
          email: 'ahmed.alotaibi@wazin.law'
        });
        setJobInfo({
          employeeNumber: 'EMP-001',
          position: 'محامي أول',
          department: 'القسم القانوني',
          grade: 'أول',
          directManager: 'EMP-005',
          workType: 'full-time',
          attendanceSystem: 'fingerprint',
          workLocation: 'office',
          hireDate: '2020-01-15',
          contractType: 'unlimited',
          employmentStatus: 'active',
          workDays: 'sunday-thursday',
          workHours: '08:00-17:00'
        });
        setSalaryInfo({
          basicSalary: '15000',
          housingAllowance: '2000',
          transportAllowance: '1000',
          otherAllowances: '500',
          notes: 'بدلات إضافية للعمل الميداني'
        });
        setBankInfo({
          bankName: 'البنك الأهلي',
          accountNumber: '1234567890',
          iban: 'SA0380000000608010167519'
        });
        setEmergencyContact({
          name: 'محمد العتيبي',
          phone: '+966507654321',
          relationship: 'أخ'
        });
        setDocuments([
          { id: 1, name: 'الهوية الوطنية.pdf', type: 'identity', size: '2.3 MB', isPrimary: true },
          { id: 2, name: 'الشهادة الجامعية.pdf', type: 'certificate', size: '1.8 MB', isPrimary: false }
        ]);
        setLoading(false);
      }, 500);
    }
  }, [isEditMode, id]);

  // Mock data for dropdowns
  const departments = ['القسم القانوني', 'القسم الإداري', 'القسم المالي', 'الموارد البشرية', 'التسويق'];
  const grades = ['متدرب', 'ثالث', 'ثاني', 'أول', 'أول ممتاز', 'مدير'];
  const managers = [
    { id: 'EMP-005', name: 'سعود إبراهيم المطيري - مدير الموارد البشرية' },
    { id: 'EMP-003', name: 'خالد عبدالله الشمري - محاسب' }
  ];
  const banks = ['البنك الأهلي', 'بنك الراجحي', 'بنك الرياض', 'البنك السعودي الفرنسي', 'بنك ساب'];
  const documentTypes = [
    { value: 'identity', label: 'الهوية الوطنية / الإقامة' },
    { value: 'passport', label: 'جواز السفر' },
    { value: 'certificate', label: 'الشهادة العلمية' },
    { value: 'cv', label: 'السيرة الذاتية' },
    { value: 'contract', label: 'العقد' },
    { value: 'other', label: 'مستند آخر' }
  ];

  // Validate IBAN
  const validateIBAN = (iban) => {
    const ibanPattern = /^SA\d{22}$/;
    return ibanPattern.test(iban);
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadingDoc(true);
      // Mock upload
      setTimeout(() => {
        const newDoc = {
          id: Date.now(),
          name: file.name,
          type: 'other',
          size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
          isPrimary: false
        };
        setDocuments(prev => [...prev, newDoc]);
        setUploadingDoc(false);
      }, 1000);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = (docId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستند؟')) {
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
    }
  };

  // Handle set primary document
  const handleSetPrimaryDocument = (docId) => {
    setDocuments(prev => prev.map(doc => ({
      ...doc,
      isPrimary: doc.id === docId
    })));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Personal Info validation
    if (!personalInfo.fullName.trim()) newErrors.fullName = 'الاسم مطلوب';
    if (!personalInfo.nationalId.trim()) newErrors.nationalId = 'رقم الهوية مطلوب';
    if (!personalInfo.birthDate) newErrors.birthDate = 'تاريخ الميلاد مطلوب';
    if (!personalInfo.phone.trim()) newErrors.phone = 'رقم الجوال مطلوب';
    if (!personalInfo.email.trim()) newErrors.email = 'البريد الإلكتروني مطلوب';

    // Job Info validation
    if (!jobInfo.employeeNumber.trim()) newErrors.employeeNumber = 'الرقم الوظيفي مطلوب';
    if (!jobInfo.position.trim()) newErrors.position = 'المسمى الوظيفي مطلوب';
    if (!jobInfo.department) newErrors.department = 'القسم مطلوب';
    if (!jobInfo.hireDate) newErrors.hireDate = 'تاريخ التعيين مطلوب';

    // Bank Info validation
    if (bankInfo.iban && !validateIBAN(bankInfo.iban)) {
      newErrors.iban = 'رقم IBAN غير صحيح (يجب أن يبدأ بـ SA ويتكون من 24 حرف)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('يرجى تصحيح الأخطاء في النموذج');
      return;
    }

    setSaving(true);
    // Mock save
    setTimeout(() => {
      setSaving(false);
      alert(isEditMode ? 'تم تحديث بيانات الموظف بنجاح' : 'تم إضافة الموظف بنجاح');
      navigate('/admin/hr/employees');
    }, 1500);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل البيانات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission(isEditMode ? 'hr.edit' : 'hr.create')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية {isEditMode ? 'تعديل' : 'إضافة'} الموظفين</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/hr/employees')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة لقائمة الموظفين</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <User className="w-8 h-8" />
                <span>{isEditMode ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}</span>
              </h1>
              <p className="text-gray-600">
                {isEditMode ? 'تحديث المعلومات الشخصية والوظيفية للموظف' : 'إدخال بيانات الموظف الجديد'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 1. Personal Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <User className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">البيانات الشخصية</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الاسم الكامل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={personalInfo.fullName}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.fullName ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="أدخل الاسم الكامل"
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* National ID */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الهوية / الإقامة <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={personalInfo.nationalId}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, nationalId: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.nationalId ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="1234567890"
                />
                {errors.nationalId && <p className="text-red-500 text-xs mt-1">{errors.nationalId}</p>}
              </div>

              {/* Birth Date + Age */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  تاريخ الميلاد <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={personalInfo.birthDate}
                    onChange={(e) => setPersonalInfo({ ...personalInfo, birthDate: e.target.value })}
                    className={`flex-1 px-4 py-2 border-2 rounded-lg focus:outline-none ${
                      errors.birthDate ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                  />
                  {calculatedAge && (
                    <div className="px-4 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg text-blue-700 font-bold text-sm flex items-center">
                      {calculatedAge}
                    </div>
                  )}
                </div>
                {errors.birthDate && <p className="text-red-500 text-xs mt-1">{errors.birthDate}</p>}
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الجنسية</label>
                <input
                  type="text"
                  value={personalInfo.nationality}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, nationality: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="سعودي"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الجنس</label>
                <select
                  value={personalInfo.gender}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, gender: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="male">ذكر</option>
                  <option value="female">أنثى</option>
                </select>
              </div>

              {/* Marital Status */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الحالة الاجتماعية</label>
                <select
                  value={personalInfo.maritalStatus}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, maritalStatus: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="single">أعزب</option>
                  <option value="married">متزوج</option>
                  <option value="divorced">مطلق</option>
                  <option value="widowed">أرمل</option>
                </select>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">العنوان</label>
                <input
                  type="text"
                  value={personalInfo.address}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, address: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="المدينة، الحي، الشارع"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={personalInfo.phone}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.phone ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="+966501234567"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  البريد الإلكتروني <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.email ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="example@wazin.law"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          {/* 2. Job Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <Briefcase className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">البيانات الوظيفية</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Employee Number */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الرقم الوظيفي <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={jobInfo.employeeNumber}
                  onChange={(e) => setJobInfo({ ...jobInfo, employeeNumber: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.employeeNumber ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="EMP-0001"
                />
                {errors.employeeNumber && <p className="text-red-500 text-xs mt-1">{errors.employeeNumber}</p>}
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  المسمى الوظيفي <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={jobInfo.position}
                  onChange={(e) => setJobInfo({ ...jobInfo, position: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.position ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="محامي أول"
                />
                {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position}</p>}
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  القسم <span className="text-red-500">*</span>
                </label>
                <select
                  value={jobInfo.department}
                  onChange={(e) => setJobInfo({ ...jobInfo, department: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.department ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                >
                  <option value="">اختر القسم</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department}</p>}
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الدرجة الوظيفية</label>
                <select
                  value={jobInfo.grade}
                  onChange={(e) => setJobInfo({ ...jobInfo, grade: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="">اختر الدرجة</option>
                  {grades.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              {/* Direct Manager */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">المدير المباشر</label>
                <select
                  value={jobInfo.directManager}
                  onChange={(e) => setJobInfo({ ...jobInfo, directManager: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="">اختر المدير</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                  ))}
                </select>
              </div>

              {/* Work Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نوع الدوام</label>
                <select
                  value={jobInfo.workType}
                  onChange={(e) => setJobInfo({ ...jobInfo, workType: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="full-time">دوام كامل</option>
                  <option value="part-time">دوام جزئي</option>
                  <option value="remote">عن بُعد</option>
                </select>
              </div>

              {/* Attendance System */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نظام الحضور</label>
                <select
                  value={jobInfo.attendanceSystem}
                  onChange={(e) => setJobInfo({ ...jobInfo, attendanceSystem: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="fingerprint">بصمة</option>
                  <option value="card">كرت</option>
                  <option value="manual">يدوي</option>
                </select>
              </div>

              {/* Work Location */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">موقع العمل</label>
                <select
                  value={jobInfo.workLocation}
                  onChange={(e) => setJobInfo({ ...jobInfo, workLocation: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="office">المكتب الرئيسي</option>
                  <option value="remote">عن بُعد</option>
                  <option value="hybrid">مختلط</option>
                </select>
              </div>

              {/* Hire Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  تاريخ التعيين <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={jobInfo.hireDate}
                  onChange={(e) => setJobInfo({ ...jobInfo, hireDate: e.target.value })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.hireDate ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                />
                {errors.hireDate && <p className="text-red-500 text-xs mt-1">{errors.hireDate}</p>}
              </div>

              {/* Contract Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">نوع العقد</label>
                <select
                  value={jobInfo.contractType}
                  onChange={(e) => setJobInfo({ ...jobInfo, contractType: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="unlimited">غير محدد المدة</option>
                  <option value="limited">محدد المدة</option>
                  <option value="remote-contract">عقد عمل عن بُعد</option>
                  <option value="part-time-contract">عقد جزئي</option>
                </select>
              </div>

              {/* Employment Status */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الحالة الوظيفية</label>
                <select
                  value={jobInfo.employmentStatus}
                  onChange={(e) => setJobInfo({ ...jobInfo, employmentStatus: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="active">نشط</option>
                  <option value="trial">تحت التجربة</option>
                  <option value="suspended">موقوف</option>
                  <option value="resigned">مستقيل</option>
                </select>
              </div>

              {/* Work Days */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">أيام العمل الأسبوعية</label>
                <select
                  value={jobInfo.workDays}
                  onChange={(e) => setJobInfo({ ...jobInfo, workDays: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="sunday-thursday">الأحد - الخميس</option>
                  <option value="sunday-friday">الأحد - الجمعة</option>
                  <option value="monday-friday">الإثنين - الجمعة</option>
                </select>
              </div>

              {/* Work Hours */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">ساعات الدوام</label>
                <input
                  type="text"
                  value={jobInfo.workHours}
                  onChange={(e) => setJobInfo({ ...jobInfo, workHours: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="08:00-17:00"
                />
              </div>
            </div>
          </div>

          {/* 3. Salary Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <DollarSign className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">الراتب والبدلات</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Basic Salary */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الراتب الأساسي (ر.س)</label>
                <input
                  type="number"
                  value={salaryInfo.basicSalary}
                  onChange={(e) => setSalaryInfo({ ...salaryInfo, basicSalary: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="15000"
                />
              </div>

              {/* Housing Allowance */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدل السكن (ر.س)</label>
                <input
                  type="number"
                  value={salaryInfo.housingAllowance}
                  onChange={(e) => setSalaryInfo({ ...salaryInfo, housingAllowance: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="2000"
                />
              </div>

              {/* Transport Allowance */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدل النقل (ر.س)</label>
                <input
                  type="number"
                  value={salaryInfo.transportAllowance}
                  onChange={(e) => setSalaryInfo({ ...salaryInfo, transportAllowance: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="1000"
                />
              </div>

              {/* Other Allowances */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدلات أخرى (ر.س)</label>
                <input
                  type="number"
                  value={salaryInfo.otherAllowances}
                  onChange={(e) => setSalaryInfo({ ...salaryInfo, otherAllowances: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="500"
                />
              </div>

              {/* Total Salary Display */}
              <div className="md:col-span-2">
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2d5ab8] rounded-lg p-4 text-white">
                  <p className="text-sm mb-1">إجمالي الراتب</p>
                  <p className="text-3xl font-black">
                    {(
                      parseFloat(salaryInfo.basicSalary || 0) +
                      parseFloat(salaryInfo.housingAllowance || 0) +
                      parseFloat(salaryInfo.transportAllowance || 0) +
                      parseFloat(salaryInfo.otherAllowances || 0)
                    ).toLocaleString()} ر.س
                  </p>
                </div>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">ملاحظات</label>
                <textarea
                  value={salaryInfo.notes}
                  onChange={(e) => setSalaryInfo({ ...salaryInfo, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="أي ملاحظات إضافية حول الراتب والبدلات"
                ></textarea>
              </div>
            </div>
          </div>

          {/* 4. Bank Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <Building2 className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">البيانات البنكية</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bank Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">اسم البنك</label>
                <select
                  value={bankInfo.bankName}
                  onChange={(e) => setBankInfo({ ...bankInfo, bankName: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="">اختر البنك</option>
                  {banks.map((bank) => (
                    <option key={bank} value={bank}>{bank}</option>
                  ))}
                </select>
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">رقم الحساب</label>
                <input
                  type="text"
                  value={bankInfo.accountNumber}
                  onChange={(e) => setBankInfo({ ...bankInfo, accountNumber: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="1234567890"
                />
              </div>

              {/* IBAN */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">IBAN</label>
                <input
                  type="text"
                  value={bankInfo.iban}
                  onChange={(e) => setBankInfo({ ...bankInfo, iban: e.target.value.toUpperCase() })}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.iban ? 'border-red-500' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="SA0380000000608010167519"
                  maxLength="24"
                />
                {errors.iban && <p className="text-red-500 text-xs mt-1">{errors.iban}</p>}
                {bankInfo.iban && validateIBAN(bankInfo.iban) && (
                  <div className="flex items-center gap-2 mt-2 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>رقم IBAN صحيح</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 5. Emergency Contact */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <Phone className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">معلومات الطوارئ</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Emergency Contact Name */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الاسم</label>
                <input
                  type="text"
                  value={emergencyContact.name}
                  onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="اسم جهة الاتصال"
                />
              </div>

              {/* Emergency Contact Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">رقم الهاتف</label>
                <input
                  type="tel"
                  value={emergencyContact.phone}
                  onChange={(e) => setEmergencyContact({ ...emergencyContact, phone: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="+966501234567"
                />
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">العلاقة</label>
                <input
                  type="text"
                  value={emergencyContact.relationship}
                  onChange={(e) => setEmergencyContact({ ...emergencyContact, relationship: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="أخ، والد، زوج، إلخ"
                />
              </div>
            </div>
          </div>

          {/* 6. Documents */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-gray-200">
              <FileText className="w-6 h-6 text-[#1E3A8A]" />
              <h2 className="text-xl font-black text-gray-800">المستندات</h2>
            </div>

            {/* Upload Button */}
            <div className="mb-4">
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#C8A870] text-white rounded-lg font-bold cursor-pointer hover:bg-[#b8986a] transition-all">
                <Upload className="w-4 h-4" />
                <span>رفع مستند</span>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingDoc || !hasPermission('hr.upload')}
                />
              </label>
              {uploadingDoc && (
                <span className="mr-3 text-sm text-gray-600">جاري الرفع...</span>
              )}
            </div>

            {/* Documents List */}
            {documents.length > 0 ? (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      doc.isPrimary ? 'border-[#C8A870] bg-yellow-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className={`w-6 h-6 ${doc.isPrimary ? 'text-[#C8A870]' : 'text-gray-400'}`} />
                      <div className="flex-1">
                        <p className="font-bold text-gray-800">{doc.name}</p>
                        <p className="text-xs text-gray-500">{doc.size}</p>
                      </div>
                      {doc.isPrimary && (
                        <span className="px-2 py-1 bg-[#C8A870] text-white text-xs rounded-full font-bold">
                          رئيسي
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => alert(`معاينة: ${doc.name}`)}
                        className="p-2 text-[#1E3A8A] hover:bg-blue-50 rounded-lg transition-colors"
                        title="معاينة"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!doc.isPrimary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimaryDocument(doc.id)}
                          className="p-2 text-[#C8A870] hover:bg-yellow-50 rounded-lg transition-colors"
                          title="تعيين كرئيسي"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="حذف"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>لم يتم رفع أي مستندات بعد</p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/hr/employees')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{isEditMode ? 'حفظ التعديلات' : 'إضافة الموظف'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default EmployeeFormPage;